import React, { useState } from 'react';
import { getToken } from '../utils/getToken';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

const ChangePassword = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [response, setResponse] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleChangePassword = async () => {
        // Validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            setResponse('Please fill in all fields');
            setIsSuccess(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setResponse('New passwords do not match');
            setIsSuccess(false);
            return;
        }

        if (newPassword.length < 6) {
            setResponse('New password must be at least 6 characters');
            setIsSuccess(false);
            return;
        }

        setIsLoading(true);
        setResponse('');

        const token = getToken();
        if (!token) {
            setResponse('Error: No token found');
            setIsSuccess(false);
            setIsLoading(false);
            return;
        }

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", `Bearer ${token}`);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword
            }),
            redirect: "follow"
        };

        try {
            const response = await fetch(`${baseURL}/profile/change-password`, requestOptions);
            const result = await response.json();

            if (response.ok) {
                setResponse('✅ Password changed successfully!');
                setIsSuccess(true);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                setResponse(result.message || '❌ Failed to change password');
                setIsSuccess(false);
            }
        } catch (error) {
            console.error('Error:', error);
            setResponse('❌ Network error. Please try again.');
            setIsSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='w-full max-w-lg'>
            <div className='bg-white rounded-2xl border border-gray-100 p-4 lg:p-6'>
                <div className='flex items-center gap-3 mb-4'>
                    <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                    <div>
                        <h2 className='text-lg font-bold text-gray-800'>Change Password</h2>
                        <p className='text-xs text-gray-400'>Update your account password</p>
                    </div>
                </div>

                {response && (
                    <div className={`p-3 rounded-xl text-sm mb-4 ${isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        {response}
                    </div>
                )}

                <div className='space-y-4'>
                    {/* Current Password */}
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            Current Password
                        </label>
                        <div className='relative'>
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={e => setCurrentPassword(e.target.value)}
                                className="w-full px-4 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 text-gray-800"
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showCurrentPassword ? <VisibilityOff className='text-sm' /> : <Visibility className='text-sm' />}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            New Password
                        </label>
                        <div className='relative'>
                            <input
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                className="w-full px-4 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 text-gray-800"
                                placeholder="Enter new password (min 6 characters)"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showNewPassword ? <VisibilityOff className='text-sm' /> : <Visibility className='text-sm' />}
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            Confirm New Password
                        </label>
                        <div className='relative'>
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 text-gray-800"
                                placeholder="Confirm new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirmPassword ? <VisibilityOff className='text-sm' /> : <Visibility className='text-sm' />}
                            </button>
                        </div>
                    </div>

                    <button 
                        onClick={handleChangePassword} 
                        disabled={isLoading}
                        className={`w-full py-2.5 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                            isLoading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-[#069125] hover:bg-[#057a1f] hover:shadow-lg hover:shadow-[#069125]/30'
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Updating...
                            </>
                        ) : (
                            '🔒 Change Password'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;